/**
 * Level mapping utilities for converting between display format (P1-P6, Sec 1-4) and internal format (primary_1, secondary_1, etc.).
 * Provides bidirectional mapping for enrollment forms and data processing.
 * Update when adding new education levels or modifying level display formats.
 */

import { EXAM_LEVELS } from '@shared/examLevels';
import { ORG_LEVEL_DISPLAY_LABELS } from '@shared/orgLevels';

/**
 * Mapping from internal format to display format. Derived from the shared table
 * so the server's calendar-event titles and the app's labels cannot drift.
 */
const INTERNAL_TO_DISPLAY: Record<string, string> = {
  ...ORG_LEVEL_DISPLAY_LABELS,
};

/**
 * Mapping from display format to internal format.
 */
const DISPLAY_TO_INTERNAL: Record<string, string> = Object.fromEntries(
  Object.entries(INTERNAL_TO_DISPLAY).map(([internal, display]) => [
    display,
    internal,
  ])
);

/**
 * All valid internal level formats.
 */
const VALID_INTERNAL_LEVELS = Object.keys(INTERNAL_TO_DISPLAY);

/**
 * All valid display level formats.
 */
const VALID_DISPLAY_LEVELS = Object.values(INTERNAL_TO_DISPLAY);

/**
 * Convert internal level format to display format.
 */
export function internalToDisplay(internalLevel: string): string | null {
  if (!internalLevel || typeof internalLevel !== 'string') {
    return null;
  }
  return INTERNAL_TO_DISPLAY[internalLevel] || null;
}

/**
 * Long-form label for a primary org level, e.g. `primary_1` → `"Primary 1"`.
 * The practice/insights "Your syllabus" chip uses this instead of the compact
 * `internalToDisplay` ("P1"): primary carries no exam suffix, so the short form
 * looked insubstantial on its own. Returns `null` for non-primary or unknown
 * levels, so callers fall back to the compact stream label.
 */
export function primaryLongDisplay(internalLevel: string): string | null {
  if (!isPrimaryLevel(internalLevel)) return null;
  return `Primary ${internalLevel.slice('primary_'.length)}`;
}

/**
 * Format a set of internal org levels (e.g. the union of a widened KG node's
 * `levelScope` org levels) into one canonical display label like
 * `"Sec 2 · Sec 3"`. Deduped, unknown levels dropped, ordered P1 → Sec 4.
 * Returns `''` when nothing maps, so callers can fall back to a legacy tag.
 *
 * Prefer this over a node's frozen `levelTag` when the display should reflect
 * cross-level reuse: `levelTag` is stamped once at seed time and is not
 * updated when a node is widened into another stream.
 */
export function orgLevelsToDisplay(internalLevels: string[]): string {
  return Array.from(new Set(internalLevels))
    .filter(level => INTERNAL_TO_DISPLAY[level])
    .sort(
      (a, b) =>
        VALID_INTERNAL_LEVELS.indexOf(a) - VALID_INTERNAL_LEVELS.indexOf(b)
    )
    .map(level => INTERNAL_TO_DISPLAY[level])
    .join(' · ');
}

/**
 * Org levels at which the Elementary/Additional Mathematics split exists.
 * Lower secondary (Sec 1–2) has a single maths track, so the subject tag is
 * meaningless there; primary levels never carry it.
 */
const UPPER_SEC_ORG_LEVELS = new Set(['secondary_3', 'secondary_4']);

export interface LevelScopeRuleLike {
  orgLevels: string[];
  examLevels: string[];
}

/**
 * Expand a KG node's `levelScope` rules into ordered, deduped scope chips —
 * one per (org × exam) pair a student stream actually reaches, e.g.
 * `["Sec 2 IP", "Sec 3 G3 E-Math"]`. Prefer this over the flattened
 * `orgLevels`/`examLevels` unions, which can't reconstruct the pairing.
 *
 * The maths subject (E-Math / A-Math) is folded into a chip only for upper
 * secondary (Sec 3–4), where the E/A split is real; a node tagged with both
 * subjects yields one chip per subject. Pairs order P1 → Sec 4, then by
 * canonical exam order; unknown org levels are dropped.
 */
export function levelScopeToDisplayChips(
  levelScope: readonly LevelScopeRuleLike[],
  mathSubjects: readonly string[] = []
): string[] {
  const pairs = new Map<string, { org: string; exam: string }>();
  for (const rule of levelScope ?? []) {
    for (const org of rule.orgLevels ?? []) {
      if (!INTERNAL_TO_DISPLAY[org]) continue;
      for (const exam of rule.examLevels ?? []) {
        pairs.set(`${org}:${exam}`, { org, exam });
      }
    }
  }
  const ordered = Array.from(pairs.values()).sort((a, b) => {
    const orgDelta =
      VALID_INTERNAL_LEVELS.indexOf(a.org) -
      VALID_INTERNAL_LEVELS.indexOf(b.org);
    if (orgDelta !== 0) return orgDelta;
    return (
      EXAM_LEVELS.indexOf(a.exam as (typeof EXAM_LEVELS)[number]) -
      EXAM_LEVELS.indexOf(b.exam as (typeof EXAM_LEVELS)[number])
    );
  });
  const chips: string[] = [];
  for (const { org, exam } of ordered) {
    const base = `${INTERNAL_TO_DISPLAY[org]} ${exam}`;
    if (UPPER_SEC_ORG_LEVELS.has(org) && mathSubjects.length > 0) {
      for (const subject of mathSubjects) chips.push(`${base} ${subject}`);
    } else {
      chips.push(base);
    }
  }
  return chips;
}

/**
 * Convert display level format to internal format.
 */
export function displayToInternal(displayLevel: string): string | null {
  if (!displayLevel || typeof displayLevel !== 'string') {
    return null;
  }
  return DISPLAY_TO_INTERNAL[displayLevel] || null;
}

/**
 * Check if a level is a valid internal format.
 */
function isValidInternalLevel(level: string): boolean {
  return typeof level === 'string' && VALID_INTERNAL_LEVELS.includes(level);
}

/**
 * Check if a level is a valid display format.
 */
function _isValidDisplayLevel(level: string): boolean {
  return typeof level === 'string' && VALID_DISPLAY_LEVELS.includes(level);
}

/**
 * Check if a level is a secondary level (Sec 1-4).
 */
export function isSecondaryLevel(internalLevel: string): boolean {
  if (!isValidInternalLevel(internalLevel)) {
    return false;
  }
  return internalLevel.startsWith('secondary_');
}

/**
 * Check if a level is a primary level (P1-P6).
 */
function isPrimaryLevel(internalLevel: string): boolean {
  if (!isValidInternalLevel(internalLevel)) {
    return false;
  }
  return internalLevel.startsWith('primary_');
}

/**
 * Get all available levels in display format, ordered from P1 to Sec 4.
 */
export function getAllDisplayLevels(): string[] {
  return [...VALID_DISPLAY_LEVELS];
}

/**
 * Get all available levels in internal format, ordered from primary_1 to secondary_4.
 */
export function getAllInternalLevels(): string[] {
  return [...VALID_INTERNAL_LEVELS];
}

/**
 * Get all primary levels in display format, ordered from P1 to P6.
 */
function _getPrimaryDisplayLevels(): string[] {
  return VALID_DISPLAY_LEVELS.filter((_, index) =>
    VALID_INTERNAL_LEVELS[index].startsWith('primary_')
  );
}

/**
 * Get all secondary levels in display format, ordered from Sec 1 to Sec 4.
 */
function _getSecondaryDisplayLevels(): string[] {
  return VALID_DISPLAY_LEVELS.filter((_, index) =>
    VALID_INTERNAL_LEVELS[index].startsWith('secondary_')
  );
}

/**
 * Check if a display level is an upper secondary level (Sec 3 or Sec 4).
 * Used to conditionally show the math subject selection field.
 */
export function isUpperSecondaryDisplayLevel(displayLevel: string): boolean {
  return displayLevel === 'Sec 3' || displayLevel === 'Sec 4';
}

/**
 * Check if a display level is a primary level (P1-P6).
 */
export function isPrimaryDisplayLevel(displayLevel: string): boolean {
  if (!displayLevel || typeof displayLevel !== 'string') {
    return false;
  }
  const internalLevel = displayToInternal(displayLevel);
  return internalLevel ? isPrimaryLevel(internalLevel) : false;
}
